import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { scheduleAPI } from '../utils/api';
import type { CreateScheduleRequest, Schedule, UpdateScheduleRequest } from '../types/schedule';

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const emptyForm = (nurseryId: string): CreateScheduleRequest => ({
  name: '',
  dropoff_time: '08:00',
  pickup_time: '15:00',
  working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  nursery_id: nurseryId,
});

const parseWorkingDays = (value: string[] | string): string[] => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value ? value.split(',').map((day) => day.trim()).filter(Boolean) : [];
  }
};

const translateDay = (t: (key: string, options?: { defaultValue?: string }) => string, day: string): string =>
  t(day.toLowerCase(), { defaultValue: day });

const formatTimeForApi = (value: string): string => {
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const Schedules: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'schedules' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [formData, setFormData] = useState<CreateScheduleRequest>(emptyForm(''));

  const nurseryId = currentNursery?.nursery_id || '';

  const loadSchedules = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await scheduleAPI.getSchedules(nurseryId);
      setSchedules(response.schedules || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tc('messages.error'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId, tc]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm(nurseryId));
    setShowModal(true);
  };

  const openEdit = (schedule: Schedule) => {
    setEditing(schedule);
    setFormData({
      name: schedule.name,
      dropoff_time: formatTimeForApi(schedule.dropoff_time),
      pickup_time: formatTimeForApi(schedule.pickup_time),
      working_days: parseWorkingDays(schedule.working_days),
      nursery_id: nurseryId,
    });
    setShowModal(true);
  };

  const toggleDay = (day: string) => {
    setFormData((current) => ({
      ...current,
      working_days: current.working_days.includes(day)
        ? current.working_days.filter((item) => item !== day)
        : [...current.working_days, day],
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!nurseryId) return;

    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const payload: UpdateScheduleRequest = {
          name: formData.name,
          dropoff_time: formatTimeForApi(formData.dropoff_time),
          pickup_time: formatTimeForApi(formData.pickup_time),
          working_days: formData.working_days,
        };
        await scheduleAPI.updateSchedule(editing.id, payload, nurseryId);
      } else {
        await scheduleAPI.createSchedule({
          ...formData,
          nursery_id: nurseryId,
          dropoff_time: formatTimeForApi(formData.dropoff_time),
          pickup_time: formatTimeForApi(formData.pickup_time),
        });
      }
      setShowModal(false);
      await loadSchedules();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tc('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const sortedSchedules = useMemo(
    () => [...schedules].sort((a, b) => a.name.localeCompare(b.name)),
    [schedules],
  );

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h2 className="fw-bold text-primary mb-1">{t('title', { defaultValue: 'Schedules' })}</h2>
            <div className="text-muted">{currentNursery?.name || nurseryId}</div>
          </div>
          <Button onClick={openCreate} disabled={!nurseryId}>{t('addSchedule', { defaultValue: 'Add Schedule' })}</Button>
        </div>

        <ScopeIndicator resource="schedules" />
        {!nurseryId && <Alert variant="warning">{t('selectNursery', { defaultValue: 'Select a nursery to manage schedules.' })}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('scheduleName', { defaultValue: 'Schedule' })}</th>
                    <th>{t('dropoffTime', { defaultValue: 'Drop-off' })}</th>
                    <th>{t('pickupTime', { defaultValue: 'Pick-up' })}</th>
                    <th>{t('workingDays', { defaultValue: 'Days' })}</th>
                    <th className="text-end">{tc('labels.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-5"><Spinner animation="border" /></td></tr>
                  ) : sortedSchedules.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-muted py-5">{t('noneSetUp', { defaultValue: 'No schedules set up.' })}</td></tr>
                  ) : sortedSchedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td className="fw-semibold">{schedule.name}</td>
                      <td>{formatTimeForApi(schedule.dropoff_time)}</td>
                      <td>{formatTimeForApi(schedule.pickup_time)}</td>
                      <td>{parseWorkingDays(schedule.working_days).map((day) => translateDay(t, day)).join(', ') || '-'}</td>
                      <td className="text-end">
                        <Button variant="outline-primary" size="sm" onClick={() => openEdit(schedule)}>
                          {tc('buttons.edit')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Form onSubmit={handleSave}>
            <Modal.Header closeButton>
              <Modal.Title>{editing ? t('editSchedule', { defaultValue: 'Edit Schedule' }) : t('addSchedule', { defaultValue: 'Add Schedule' })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>{t('scheduleName', { defaultValue: 'Schedule name' })}</Form.Label>
                <Form.Control required value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('dropoffTime', { defaultValue: 'Drop-off time' })}</Form.Label>
                    <Form.Control required type="time" value={formData.dropoff_time} onChange={(event) => setFormData({ ...formData, dropoff_time: event.target.value })} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('pickupTime', { defaultValue: 'Pick-up time' })}</Form.Label>
                    <Form.Control required type="time" value={formData.pickup_time} onChange={(event) => setFormData({ ...formData, pickup_time: event.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Label>{t('workingDays', { defaultValue: 'Working days' })}</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {WEEK_DAYS.map((day) => (
                  <Form.Check key={day} type="checkbox" id={`day-${day}`} label={translateDay(t, day)} checked={formData.working_days.includes(day)} onChange={() => toggleDay(day)} />
                ))}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>{tc('buttons.cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? <Spinner animation="border" size="sm" /> : tc('buttons.save')}</Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Schedules;
